import { useState, FormEvent } from "react";
import { submitForm } from "@/lib/submitForm";

export default function HospitalInquiry() {
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const description = (form.elements.namedItem("description") as HTMLTextAreaElement)?.value ?? "";
    if (description.length > 500) return;

    setLoading(true);
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => { body.append(key, value.toString()); });

    try {
      await submitForm("hospital-pharmacy", body);
      setSubmitted(true);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="inquiry" className="py-24 bg-teal-900">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
          {/* Left copy */}
          <div>
            <span className="text-teal-400 text-xs font-bold uppercase tracking-widest">Free Consultation</span>
            <h2 className="text-white text-3xl md:text-4xl font-extrabold mt-3 mb-6 leading-tight uppercase">
              Request a Free Quote<br />
              <span className="text-teal-400">for Your Facility</span>
            </h2>
            <p className="text-white/70 text-sm leading-relaxed mb-8">
              Tell us about your healthcare facility's needs and we'll have one of our
              medical equipment specialists contact you within 24 hours with a tailored proposal.
            </p>
            <div className="space-y-4">
              {[
                { icon: "ri-phone-line", text: "+254 722 254 640" },
                { icon: "ri-mail-line", text: "info@isman.co.ke" },
                { icon: "ri-map-pin-2-line", text: "Nairobi Industrial Area, Kenya" },
              ].map((item) => (
                <div key={item.text} className="flex items-center gap-3">
                  <div className="w-8 h-8 flex items-center justify-center bg-teal-800 rounded-lg flex-shrink-0">
                    <i className={`${item.icon} text-teal-400`}></i>
                  </div>
                  <p className="text-white/80 text-sm">{item.text}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Form */}
          <div className="bg-white rounded-lg p-8">
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-12 text-center">
                <div className="w-16 h-16 flex items-center justify-center bg-teal-50 rounded-full mb-4">
                  <i className="ri-check-double-line text-3xl text-teal-600"></i>
                </div>
                <h3 className="text-gray-900 font-extrabold text-lg uppercase mb-2">Enquiry Received!</h3>
                <p className="text-gray-500 text-sm">Our team will contact you within 24 hours with a tailored quote.</p>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-isman-form>
                <h3 className="text-gray-900 font-extrabold text-base uppercase tracking-wide mb-6">
                  Equipment Enquiry Form
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                      Full Name *
                    </label>
                    <input
                      name="fullName"
                      type="text"
                      required
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                      placeholder="Dr. Jane Wanjiku"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                      Email *
                    </label>
                    <input
                      name="email"
                      type="email"
                      required
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                      placeholder="jane@hospital.co.ke"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                      Phone Number *
                    </label>
                    <input
                      name="phone"
                      type="tel"
                      required
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                      placeholder="+254 7XX XXX XXX"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                      Facility Type
                    </label>
                    <select
                      name="facilityType"
                      className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-teal-500 cursor-pointer"
                    >
                      <option value="">Select type</option>
                      <option>Public Hospital</option>
                      <option>Private Hospital</option>
                      <option>Clinic / Health Centre</option>
                      <option>Pharmacy</option>
                      <option>Laboratory</option>
                      <option>Dental Practice</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                    Equipment Required
                  </label>
                  <select
                    name="equipmentRequired"
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-teal-500 cursor-pointer"
                  >
                    <option value="">Select equipment</option>
                    <option>Pharmacy Dispensing Counter</option>
                    <option>Medicine Storage Cabinets</option>
                    <option>Instrument Trolleys</option>
                    <option>Sterile Store Shelving</option>
                    <option>Nurse Station Counter</option>
                    <option>Mortuary / Lab Equipment</option>
                    <option>Multiple / Custom Project</option>
                  </select>
                </div>
                <div className="mb-4">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                    Facility Location *
                  </label>
                  <input
                    name="location"
                    type="text"
                    required
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-teal-500"
                    placeholder="e.g. Westlands, Nairobi"
                  />
                </div>
                <div className="mb-6">
                  <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wide mb-1">
                    Project Description
                  </label>
                  <textarea
                    name="description"
                    rows={4}
                    maxLength={500}
                    onChange={(e) => setCharCount(e.target.value.length)}
                    className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-teal-500 resize-none"
                    placeholder="Describe the equipment you need, dimensions, quantity, and any special requirements..."
                  ></textarea>
                  <p className={`text-xs mt-1 text-right ${charCount > 450 ? "text-red-400" : "text-gray-400"}`}>
                    {charCount}/500
                  </p>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-teal-600 hover:bg-teal-500 disabled:opacity-60 text-white font-bold py-3 rounded-md uppercase text-sm tracking-wider transition-colors cursor-pointer whitespace-nowrap"
                >
                  {loading ? "Sending..." : "Send Enquiry"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}
